import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["id", "isNew", "schema", "isTouched"],
  _excluded2 = ["isTouched", "id"],
  _excluded3 = ["schema"],
  _excluded4 = ["id", "isNew", "schema", "isTouched"],
  _excluded5 = ["isTouched", "id"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useMemo, memo, useEffect, useReducer, useCallback, useRef, useState } from 'react';
import { LANGUAGES } from "../../../lib/helpers";
import { useWeb5Context } from "../../../lib/Web5Context";
import { getTable, createTable, updateTable } from "../../../lib/services/valueLists";
import { getSchema } from "../../../lib/services/schemas";
import ForceForm, { validate, flatList, getDefaults } from "../../../components/ForceForm";
import adapt from "../../../components/ForceForm/lib/adapt";
import HyperDrawer from "../../../components/HyperDrawer";
import AdviceRelatedFields from "./AdviceRelatedFields";
import ValueListValues from "./ValueListValues/index";
import { showErrorToast } from "../../../lib/errors";
import { normalizeFieldName } from "../../../utils/string";
import { successToast } from "../../../utils/toast";
const FORM_SCHEMA = {
  required: ['name', 'description'],
  properties: {
    apiNameV4: {
      readOnly: true
    },
    name: {
      maxLength: 200,
      type: ['string']
    },
    description: {
      maxLength: 200,
      type: ['string']
    },
    isEditable: {
      type: ['boolean'],
      default: true
    }
  },
  layout: {
    id: 'valueList',
    uiGrid: 'linearVertical',
    composition: [{
      uiType: 'section',
      uiGrid: 'linearVertical',
      label: 'cfm_label_info',
      expandable: true,
      composition: [{
        id: 'description',
        label: 'cfm_label_list_description',
        uiType: 'expandableComponent',
        helpTip: 'cfm_label_values_list_name_help',
        labelForExpand: ['cfm_label_view_more_lang', 'cfm_label_view_less_lang'],
        composition: [{
          schemaId: 'description',
          uiType: 'text',
          label: 'cfm_label_list_description',
          placeholder: 'cfm_label_spanish'
        }, ...LANGUAGES.map(language => ({
          label: `cfm_label_${language.label}`,
          schemaId: `description${language.key}`,
          uiType: 'text',
          placeholder: 'cfm_placeholder_type_value'
        }))]
      }, {
        schemaId: 'name',
        label: 'cfm_value_list_name',
        uiType: 'text',
        placeholder: 'cfm_placeholder_type_value',
        helpTip: 'cfm_label_values_list_db_name_help'
      }, {
        label: 'cfm_label_apiv4_name',
        schemaId: 'apiNameV4',
        isReadOnly: true,
        isHidden: true,
        helpTip: 'cfm_label_values_list_apiv4_name_help',
        override: {
          create: {
            isHidden: true
          }
        }
      }, {
        schemaId: 'isEditable',
        label: 'cfm_label_editable_by_user',
        uiType: 'checkbox',
        isHidden: false
      }]
    }]
  }
};
const initialState = {
  id: null,
  data: {},
  originalData: {},
  errors: {},
  isOpen: false,
  isLoading: true,
  isSaving: false,
  isForDuplicate: false,
  valueListOrder: [],
  mappedValueListOrder: null,
  hasOrdered: false
};
function reducer(state, action) {
  switch (action.type) {
    case 'init':
      return {
        id: action.id,
        originalData: _objectSpread(_objectSpread({
          isNew: !action.id
        }, getDefaults(action.schema)), action.initialData || {}),
        data: _objectSpread(_objectSpread({
          isNew: !action.id
        }, getDefaults(action.schema)), action.initialData || {}),
        validationData: _objectSpread(_objectSpread({
          isNew: !action.id
        }, getDefaults(action.schema)), action.initialData || {}),
        errors: {},
        isOpen: true,
        isLoading: true,
        isForDuplicate: action.isForDuplicate
      };
    case 'close':
      return _objectSpread(_objectSpread({}, state), {}, {
        isSaving: false,
        isOpen: false
      });
    case 'changeIds':
      return _objectSpread(_objectSpread({}, state), {}, {
        validationData: _objectSpread(_objectSpread({}, state.data), {}, {
          ids: action.ids
        }),
        originalData: _objectSpread(_objectSpread({}, state.originalData), {}, {
          ids: action.originalIds
        })
      });
    case 'setHasOrdered':
      return _objectSpread(_objectSpread({}, state), {}, {
        hasOrdered: true
      });
    case 'changeData':
      const finalOriginalData = action.originalData || state.originalData;
      return _objectSpread(_objectSpread({}, state), {}, {
        data: action.data || state.data,
        validationData: _objectSpread(_objectSpread({}, action.data), {}, {
          ids: action.ids
        }),
        originalData: _objectSpread(_objectSpread({}, finalOriginalData), {}, {
          ids: action.originalIds
        })
      });
    case 'setErrors':
      return _objectSpread(_objectSpread({}, state), {}, {
        isSaving: false,
        errors: action.errors
      });
    case 'setSaving':
      return _objectSpread(_objectSpread({}, state), {}, {
        isSaving: action.value
      });
    case 'setLoading':
      return _objectSpread(_objectSpread({}, state), {}, {
        isLoading: action.value
      });
    default:
      throw new Error('No action provided');
  }
}
const FormValueList = _ref => {
  var _permissions$valueLis2, _permissions$extraFie2, _state$data2;
  let {
    onRef,
    refreshList,
    onSave,
    onCancel,
    onStack,
    setActiveRowId,
    timeZone,
    overrides,
    onUpdate,
    initialData
  } = _ref;
  const {
    t,
    permissions,
    overridePermissions,
    fieldsToHide = [],
    fieldsToAvoidExpand = [],
    isWeb
  } = useWeb5Context({
    overrides
  });
  const [stackIndex, setStackIndex] = useState(1);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [ids, setIds] = useState([]);
  const [originalIds, setOriginalIds] = useState([]);
  const formFields = useRef();
  const valueSchema = useRef();
  const createFields = useRef([]);
  const updateFields = useRef({});
  const deleteFields = useRef([]);
  const order = useRef([]);
  const mappedOrder = useRef({});
  const overrideIsHidden = useCallback(id => Array.isArray(fieldsToHide) && fieldsToHide.includes(id), [fieldsToHide]);

  // Child method
  useEffect(() => {
    onRef && onRef({
      open(_ref2) {
        let {
          id,
          isForDuplicate,
          isEditable
        } = _ref2;
        if (state.isOpen) return;
        // Resetting refs
        valueSchema.current = null;
        formFields.current = null;
        createFields.current = [];
        updateFields.current = {};
        deleteFields.current = [];
        setStackIndex(1);
        const FINAL_SCHEMA = fieldsToHide.length ? _objectSpread(_objectSpread({}, FORM_SCHEMA), {}, {
          layout: _objectSpread(_objectSpread({}, FORM_SCHEMA.layout), {}, {
            composition: [_objectSpread(_objectSpread({}, FORM_SCHEMA.layout.composition[0]), {}, {
              composition: FORM_SCHEMA.layout.composition[0].composition.map(field => {
                field.isHidden = overrideIsHidden(field.schemaId || field.id) || field.isHidden;
                if (fieldsToAvoidExpand !== null && fieldsToAvoidExpand !== void 0 && fieldsToAvoidExpand.length) {
                  if (fieldsToAvoidExpand.includes(field.schemaId || field.id)) {
                    field.uiType = 'text';
                  }
                }
                return field;
              })
            })]
          })
        }) : FORM_SCHEMA;
        dispatch({
          type: 'init',
          id,
          isForDuplicate,
          schema: FINAL_SCHEMA,
          initialData
        });
        formFields.current = JSON.parse(JSON.stringify(FINAL_SCHEMA));
        if (!isForDuplicate && id) {
          formFields.current.layout.composition[0].composition.forEach(component => {
            if (component.schemaId === 'name' || component.schemaId === 'cfm_label_apiv4_name') component.isReadOnly = true;
          });
        }
        if (!(fieldsToHide !== null && fieldsToHide !== void 0 && fieldsToHide.includes(formFields.current.layout.composition[0].composition[2].schemaId))) {
          var _permissions$valueLis;
          formFields.current.layout.composition[0].composition[2].isHidden = !overridePermissions && !(permissions !== null && permissions !== void 0 && (_permissions$valueLis = permissions.valueList) !== null && _permissions$valueLis !== void 0 && _permissions$valueLis.show);
        }
        if (!id) {
          return getSchema('customValuelist').then(_ref3 => {
            let {
              data
            } = _ref3;
            valueSchema.current = data;
            dispatch({
              type: 'setLoading',
              value: false
            });
          }).catch(error => {
            console.error(error);
            showErrorToast(t, error);
          });
        }
        getTable(id).then(_ref4 => {
          let {
            data
          } = _ref4;
          valueSchema.current = data.schema;
          if (id && !isForDuplicate) {
            formFields.current.layout.composition[0].composition[1].isReadOnly = true;
            let hasValue = data['apiNameV4'];
            if (!hasValue) data['apiNameV4'] = t('cfm_label_value_list_available_in_api');
            formFields.current.layout.composition[0].composition[2].ishidden = false;
            formFields.current.layout.composition[0].composition[2].uiType = hasValue ? 'text' : 'static';
          }
          if (isForDuplicate) {
            data = _objectSpread(_objectSpread({}, data), {}, {
              schemaId: null,
              isNew: true,
              name: `${data.name}_copy`,
              apiNameV4: null
            });
          }
          if (data) {
            dispatch({
              type: 'changeData',
              data,
              originalData: data
            });
          }
          dispatch({
            type: 'setLoading',
            value: false
          });
        }).catch(error => {
          console.error(error);
          showErrorToast(t, error);
        });
      }
    });
  }, [overridePermissions, permissions === null || permissions === void 0 ? void 0 : (_permissions$valueLis2 = permissions.valueList) === null || _permissions$valueLis2 === void 0 ? void 0 : _permissions$valueLis2.show, onRef, stackIndex, t, fieldsToHide.length, fieldsToAvoidExpand, overrideIsHidden, initialData, fieldsToHide, state === null || state === void 0 ? void 0 : state.isOpen]);
  const mapList = useCallback(list => {
    return (list === null || list === void 0 ? void 0 : list.reduce((obj, current) => {
      obj[current.id] = current;
      return obj;
    }, {})) || {};
  }, []);
  const updateOrder = useCallback(_ref5 => {
    let {
      values,
      id
    } = _ref5;
    if (id) {
      var _order$current;
      delete mappedOrder.current[id];
      order.current = ((_order$current = order.current) === null || _order$current === void 0 ? void 0 : _order$current.filter(item => item.id !== id)) || [];
    } else {
      order.current = values;
      mappedOrder.current = mapList(values);
    }
  }, [mapList]);
  const onCleaningProps = useCallback((data, properties) => {
    const keys = Object.keys(properties);
    return Object.keys(data).reduce((newData, key) => {
      keys.forEach(item => {
        if (key === item) newData[key] = data[key];
      });
      return newData;
    }, {});
  }, []);
  const onChangeOrder = useCallback(values => {
    updateOrder({
      values
    });
    createFields.current = values.filter(v => v.isNew).map(_ref6 => {
      let {
          id,
          isNew,
          schema,
          isTouched
        } = _ref6,
        rest = _objectWithoutProperties(_ref6, _excluded);
      let cleanData = adapt(rest, valueSchema.current);
      cleanData = onCleaningProps(cleanData, valueSchema.current.properties);
      return cleanData;
    });
    updateFields.current = values.filter(v => !v.isNew).reduce((obj, v) => {
      const {
          isTouched,
          id
        } = v,
        rest = _objectWithoutProperties(v, _excluded2);
      let cleanData = adapt(rest, valueSchema.current);
      cleanData = onCleaningProps(cleanData, valueSchema.current.properties);
      obj[v.id] = cleanData;
      return obj;
    }, {});
  }, [updateOrder, onCleaningProps]);
  const onSaveDrawer = useCallback(() => {
    const id = state.data.id;
    validate(state.data, formFields.current).then(_ref7 => {
      let {
        isValid,
        errors,
        cleanData
      } = _ref7;
      if (!isValid) dispatch({
        type: 'setErrors',
        errors
      });else {
        var _data$name, _data$name2;
        dispatch({
          type: 'setSaving',
          value: true
        });

        // Removing schema from whole data
        let {
            schema
          } = cleanData,
          data = _objectWithoutProperties(cleanData, _excluded3);
        if (!data.name) {
          data.name = data.description.replace(/ /g, '_').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z_]/g, '');
        }
        if (data.isNew && (_data$name = data.name) !== null && _data$name !== void 0 && _data$name.match(/^tbl/)) {
          data.name = `Z_${data.name}`;
        } else if (data.isNew && !((_data$name2 = data.name) !== null && _data$name2 !== void 0 && _data$name2.match(/^Z_tbl/))) {
          data.name = `Z_tbl${data.name}`;
        }
        data.createFields = createFields.current.map(_ref8 => {
          let {
              id,
              isNew,
              schema,
              isTouched
            } = _ref8,
            rest = _objectWithoutProperties(_ref8, _excluded4);
          return flatList(rest, valueSchema.current);
        });
        data.updateFields = Object.keys(updateFields.current).reduce((obj, key) => {
          obj[key] = flatList(updateFields.current[key], valueSchema.current);
          return obj;
        }, {});
        data.deleteFields = deleteFields.current;
        const pr = data.isNew ? createTable(data) : updateTable(id, data);
        pr.then(result => {
          var _result$data;
          const idList = result === null || result === void 0 ? void 0 : (_result$data = result.data) === null || _result$data === void 0 ? void 0 : _result$data.id;
          refreshList && refreshList();
          setActiveRowId && setActiveRowId(null);
          dispatch({
            type: 'close'
          });
          successToast({
            t,
            text: t(data.isNew ? 'cfm_success_create_value_list' : 'cfm_success_update_value_list')
          });
          const finalData = _objectSpread({
            idList
          }, data);
          onStack && onStack(stackIndex);
          onSave && onSave(finalData);
          onUpdate && onUpdate();
        }).catch(err => {
          console.error(err);
          dispatch({
            type: 'setSaving',
            value: false
          });
          showErrorToast(t, err);
        });
      }
    });
  }, [state.data, refreshList, setActiveRowId, t, onStack, stackIndex, onSave, onUpdate]);
  const onCancelDrawer = useCallback(() => {
    dispatch({
      type: 'close'
    });
    onStack && onStack(stackIndex);
    setActiveRowId && setActiveRowId(null);
    onCancel && onCancel();
  }, [onCancel, onStack, stackIndex, setActiveRowId]);
  const modifyFieldProps = useCallback(field => {
    var _permissions$extraFie;
    let newField = field;
    switch (field.schemaId || field.id) {
      case 'isEditable':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          isHidden: !(permissions !== null && permissions !== void 0 && (_permissions$extraFie = permissions.extraFields) !== null && _permissions$extraFie !== void 0 && _permissions$extraFie.userUpdate) || overridePermissions
        });
        break;
      default:
        break;
    }
    return newField;
  }, [overridePermissions, permissions === null || permissions === void 0 ? void 0 : (_permissions$extraFie2 = permissions.extraFields) === null || _permissions$extraFie2 === void 0 ? void 0 : _permissions$extraFie2.userUpdate]);
  const onChange = useCallback((data, field) => {
    let finalData = _objectSpread({}, data);
    if (field === 'name') {
      finalData = _objectSpread(_objectSpread({}, finalData), {}, {
        name: normalizeFieldName(data === null || data === void 0 ? void 0 : data.name, null, false)
      });
      return dispatch({
        type: 'changeData',
        data: finalData
      });
    } else if (field === 'description' && fieldsToHide.includes('name') && !finalData.id) {
      finalData = _objectSpread(_objectSpread({}, finalData), {}, {
        name: normalizeFieldName(data === null || data === void 0 ? void 0 : data.description, null, false)
      });
    }
    return dispatch({
      type: 'changeData',
      data
    });
  }, [fieldsToHide]);
  const onChangeValues = useCallback(values => {
    var _state$data;
    updateOrder({
      values
    });
    const finalIds = values.map(el => el.id).sort();
    if ((_state$data = state.data) !== null && _state$data !== void 0 && _state$data.isNew) {
      dispatch({
        type: 'changeIds',
        originalIds: originalIds === null || originalIds === void 0 ? void 0 : originalIds.sort(),
        ids: finalIds
      });
      return createFields.current = values;
    }
    createFields.current = values.filter(v => v.isNew);
    updateFields.current = values.filter(v => v.isTouched && !v.isNew).reduce((obj, v) => {
      const {
          isTouched,
          id
        } = v,
        rest = _objectWithoutProperties(v, _excluded5);
      obj[v.id] = rest;
      return obj;
    }, {});
    dispatch({
      type: 'changeIds',
      originalIds: originalIds.sort(),
      ids: finalIds
    });
  }, [state.data, originalIds, updateOrder]);
  const onRemoveValues = useCallback(id => {
    let finalIds = [...ids];
    createFields.current = createFields.current.filter(field => field.id !== id);
    delete updateFields.current[id];
    deleteFields.current = [...deleteFields.current, id];
    finalIds = finalIds.filter(field => field !== id).sort();
    updateOrder({
      id
    });
    dispatch({
      type: 'changeData',
      data: state.data,
      ids: finalIds,
      originalIds: originalIds === null || originalIds === void 0 ? void 0 : originalIds.sort()
    });
  }, [state.data, ids, originalIds, updateOrder]);
  const onStackDrawer = useCallback(index => {
    setStackIndex(index);
    onStack && onStack(index + 1);
  }, [onStack]);
  const locales = useMemo(() => ({
    title: state.data.isNew ? t('cfm_title_create_value_list') : t('cfm_title_update_value_list'),
    cancel: t('cfm_action_cancel'),
    save: t('cfm_action_save')
  }), [state.data.isNew, t]);
  const showAdviceRelatedFields = !(fieldsToHide !== null && fieldsToHide !== void 0 && fieldsToHide.length) && state.data.associatedFields && !!Object.keys(state.data.associatedFields).length;
  return /*#__PURE__*/React.createElement(HyperDrawer, {
    stackIndex: stackIndex,
    isOpen: state.isOpen,
    locales: locales,
    isLoading: state.isLoading,
    isSaving: state.isSaving,
    onCancel: onCancelDrawer,
    onSave: onSaveDrawer,
    data: state.validationData,
    originalData: state.originalData,
    className: "valuelist__crud__modal"
  }, /*#__PURE__*/React.createElement(ForceForm, {
    onChange: onChange,
    values: state.data,
    errors: state.errors,
    schema: formFields.current,
    mode: state.data && state.data.id ? 'update' : 'create',
    isWeb: isWeb || false,
    modifyFieldProps: modifyFieldProps
  }), showAdviceRelatedFields && /*#__PURE__*/React.createElement(AdviceRelatedFields, {
    fields: state.data.associatedFields
  }), state.data && /*#__PURE__*/React.createElement(ValueListValues, {
    valueListName: (_state$data2 = state.data) === null || _state$data2 === void 0 ? void 0 : _state$data2.name,
    valueListId: state.id,
    list: state.data,
    schema: valueSchema.current,
    onChange: onChangeValues,
    onRemove: onRemoveValues,
    onStack: onStackDrawer,
    setIds: setIds,
    setOriginalIds: setOriginalIds,
    timeZone: timeZone,
    overrides: overrides,
    getInitialOrder: updateOrder,
    onChangeOrder: onChangeOrder,
    isForDuplicate: state.isForDuplicate
  }));
};
process.env.NODE_ENV !== "production" ? FormValueList.propTypes = {} : void 0;
export default /*#__PURE__*/memo(FormValueList);